import React from 'react';
import { UniThemeProvider } from '@planne-software/uni/themes';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR as localeText } from '@mui/x-date-pickers/locales';
import { ptBR } from 'date-fns/locale';
import { SeatMap as PlanneSeatMap, SeatMapSkeleton, I18nProvider } from '@planne-software/seat-map';
import { useEmbedPlanneSeatmap } from './useEmbedPlanneSeatmap';
import type { EmbedPlanneSeatmapProps } from './types';

/**
 * Product Booking embed: same seat map UI as {@link PlaneSeatmapRender} but closes a modal instead of
 * advancing the classic adder stepper, and does not clear Redux selection on mount.
 */
const EmbedPlanneSeatmap = (props: Readonly<EmbedPlanneSeatmapProps>) => {
  const result = useEmbedPlanneSeatmap(props);

  if (result.view === 'empty') {
    return null;
  }

  // Booking opens the seat map in a portaled Modal, outside the widget's UniTheme tree,
  // so it must carry its own provider — unlike the classic PlaneSeatmapRender path, which
  // already renders inside the theme tree.
  return (
    <UniThemeProvider
      adapterLocale={ptBR}
      dateAdapter={AdapterDateFns}
      localeText={localeText.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      {result.view === 'skeleton' ? (
        <SeatMapSkeleton showActions />
      ) : (
        <I18nProvider defaultLanguage={result.locale}>
          <PlanneSeatMap
            showActions
            loading={result.loadingSeatMap}
            seatMapOverview={result.seatMapOverview}
            onEvent={result.onEvent}
            selectedTariffs={result.seatMapSelectedTariffs}
            actions={result.actions}
          />
        </I18nProvider>
      )}
    </UniThemeProvider>
  );
};

export default EmbedPlanneSeatmap;
export type { EmbedPlanneSeatmapActions, EmbedPlanneSeatmapProps } from './types';
